import * as React from 'react';
import { generateId } from '@agilepoint/utils-common';
import { IPopupMessageProps, IPopupMessageHostProps } from './popup-message-props';
import { eventManager } from './popup-message-event-manager';
import { PopupMessageItem, IPopupMessageBaseProps } from './popup-message-item';
import { useQueue } from '../../hooks';

interface IMessageQueueItem extends IPopupMessageProps {
    id: string;
}

export const PopupMessageHost = React.memo((props: IPopupMessageHostProps) => {
    const { currentItem, clear, dequeue, enqueue } = useQueue<IMessageQueueItem>();

    const removePopup = React.useCallback((id?: string) => {
        if (id === undefined) {
            clear();
            return;
        }
        dequeue(id);
    }, [clear, dequeue]);

    const addPopups = React.useCallback((...popupsInfo: IPopupMessageProps[]) => {
        const newItems = popupsInfo.map(popup => {
            const id = popup.id || generateId();
            return { type: 'information', ...popup, id } as IMessageQueueItem;
        });
        enqueue(...newItems);
    }, [enqueue]);

    React.useEffect(() => {
        eventManager.on('showPopupMessage', addPopups);
        eventManager.on('removePopupMessage', removePopup);
        return () => {
            eventManager.off('showPopupMessage', addPopups);
            eventManager.off('removePopupMessage', removePopup);
        };
    }, [addPopups, removePopup]);

    const messageProps = React.useMemo(() => (currentItem !== null
        ? ({ ...currentItem, props, key: currentItem.id, onDismiss: removePopup })
        : {}), [currentItem, props, removePopup]) as IPopupMessageBaseProps;

    return currentItem === null ? null : React.createElement(PopupMessageItem, messageProps);
});

PopupMessageHost.displayName = 'PopupMessageHost';
